import { isIOS, isSafari } from 'react-device-detect';
import { getSetUrl, getSkvelkoSetName, prop } from './index';
import { __ } from '@bart.sk-eshop/react-i18n';

export function getImagePath(
  filename,
  { width, height } = { width: 64, height: 80 },
  resize = true,
  ogImage = false,
  notWebp = false,
  ignoreRequestHeight = false,
  resizeType: string | null = null,
  quality: number | null = null,
) {
  if (ogImage) {
    filename = filename.replace('mainImages', 'ogImages');
  }

  if (typeof filename === 'string' && filename?.startsWith('http')) {
    const params = new URL(filename).searchParams;
    if (params) {
      const query = params.get('query');
      if (query) {
        filename = query;
      }
    }
  }

  if (filename?.startsWith('product/product/')) {
    filename = filename.replace('product/product/', 'product/');
  }

  const webp = notWebp || ogImage || isSafari || isIOS ? '' : '&webp=1';

  const queryQuality = quality ? `&quality=${quality}` : ``;

  if (resize) {
    let reqHeight = `&height=${height}`;
    if (ignoreRequestHeight) {
      reqHeight = '';
    }

    let type = resizeType ? `&type=${resizeType}` : '';

    return `${process.env.REACT_APP_API_BASE_URL}/image?query=${filename}&width=${width}${reqHeight}${webp}${type}${queryQuality}`;
  } else {
    return `${process.env.REACT_APP_API_BASE_URL}/image?query=${filename}${webp}${queryQuality}`;
  }
}

export function getAbsoluteUrl(url) {
  let baseUrl = process.env.REACT_APP_URL;
  if (baseUrl && !baseUrl.startsWith('https://www.')) {
    baseUrl = baseUrl.replace(/^https?:\/\//, 'https://www.');
  }
  if (!url.startsWith('/')) {
    url = '/' + url;
  }
  return `${baseUrl}${url}`;
}

export function productMetaHandler(product, defaultTitle) {
  const productPublish = prop(product, 'publish', [])[0];

  let metaTags: any = {};
  if (productPublish && productPublish.content.id !== null) {
    metaTags = prop(productPublish.content.json_content, 'meta', {
      title: defaultTitle,
    });

    if (!metaTags.title.length) {
      metaTags.title = `${defaultTitle} - ${resolveProductName(product)}`;
    }
  } else {
    metaTags.title = defaultTitle;
  }

  if (
    metaTags &&
    metaTags.og_description &&
    !metaTags.og_description.length &&
    product &&
    product.publish
  ) {
    metaTags.og_description =
      product && product.publish && product.publish.length > 0
        ? product.publish[0].product_description
        : '';
  }

  if (
    metaTags &&
    metaTags.og_image &&
    !metaTags.og_image.length &&
    product &&
    product.picture
  ) {
    metaTags.og_image = product.picture
      ? getImagePath(product.picture, { width: 510, height: 510 }, false, true)
      : '';
    metaTags.og_image_width = metaTags.og_image_height = 1005;
  }

  if (metaTags && metaTags.og_title && !metaTags.og_title.length) {
    metaTags.og_title = metaTags.title;
  }

  if (metaTags && metaTags.og_type && !metaTags.og_type.length) {
    metaTags.og_type = 'website';
  }

  if (
    metaTags &&
    metaTags.og_url &&
    !metaTags.og_url.length &&
    product &&
    product.url
  ) {
    metaTags.og_url = getAbsoluteUrl(product.url);
  }

  return metaTags;
}

export function resolveProductName(product) {
  let publish = prop(product, 'publish');
  if (publish && Array.isArray(publish)) {
    publish = publish[0];
    // publish = publish.find(el => {
    //   return el.lang_id === lang;
    // });
  }
  if (publish) {
    return prop(publish, 'product_name', null) || product.name;
  }
  return product.name;
}

function buildProductNode(product: any): any | null {
  if (!product || !product.main_good) return null;

  const name = resolveProductName(product);

  const images: string[] = [];
  if (product.picture) {
    images.push(
      getImagePath(
        product.picture,
        { width: 800, height: 800 },
        false,
        false,
        true,
      ),
    );
  }
  if (Array.isArray(product.photogallery)) {
    for (const photo of product.photogallery.slice(0, 5)) {
      const url = getImagePath(
        photo,
        { width: 800, height: 800 },
        false,
        false,
        true,
      );
      if (!images.includes(url)) {
        images.push(url);
      }
    }
  }

  const publish = (product.publish || [])[0];
  const rawDescription =
    publish?.content?.json_content?.short_description ||
    publish?.product_description ||
    '';
  const description = rawDescription.replace(/<[^>]*>/g, '').trim();

  const mainGood = product.main_good;
  const isAvailable = mainGood.availability_id === 1;
  const price =
    mainGood.final_price != null
      ? Number(mainGood.final_price).toFixed(2)
      : null;

  const node: any = {
    '@type': 'Product',
    name,
    ...(images.length > 0 && { image: images }),
    ...(description && { description }),
    ...(product.product_plu && { sku: product.product_plu }),
    //...(product.product_ean && { gtin13: product.product_ean }),
    //brand: { '@type': 'Brand', name: 'Daffer' },
    offers: {
      '@type': 'Offer',
      url: getAbsoluteUrl(product.url),
      priceCurrency: 'EUR',
      ...(price != null && { price }),
      availability: isAvailable
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'SK',
        returnPolicyCategory:
          'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnFees: 'https://schema.org/FreeReturn',
      },
    },
  };

  return node;
}

export function buildProductJsonLd(product: any): object | null {
  const node = buildProductNode(product);
  if (!node) return null;

  return {
    '@context': 'https://schema.org',
    ...node,
  };
}

export function buildSetJsonLd(skvelkoSet: any): object | null {
  if (!skvelkoSet || !Array.isArray(skvelkoSet.productsWithCount)) return null;

  const includesObject: any[] = [];
  for (const item of skvelkoSet.productsWithCount) {
    const node = buildProductNode(item?.product);
    if (!node) continue;
    includesObject.push({
      '@type': 'TypeAndQuantityNode',
      ...(item?.count != null && { amountOfThisGood: item.count }),
      typeOfGood: node,
    });
  }

  if (includesObject.length === 0) return null;

  const setName = getSkvelkoSetName(
    skvelkoSet.name1,
    skvelkoSet.name2,
    skvelkoSet.name3,
  );

  const schoolName = skvelkoSet.schoolFullName || skvelkoSet.schoolName || '';
  const name = schoolName ? `${schoolName} - ${setName}` : setName;

  const productCount = includesObject.length;
  const description =
    `Pripravený balík školských potrieb a pomôcok na nový školský rok` +
    `${schoolName ? ` pre ${schoolName}` : ''}. ` +
    `Sada obsahuje ${setName}. ` +
    `Obsahuje ${productCount} odporúčaných položiek zostavených presne ` +
    `podľa zoznamu školy. Celý zoznam pridáte do košíka jedným kliknutím ` +
    `a ušetríte čas s nakupovaním pomôcok pre žiaka.`;

  return {
    '@context': 'https://schema.org',
    '@type': 'ProductCollection',
    name,
    description,
    category: 'Školské potreby a pomôcky',
    url: getAbsoluteUrl(getSetUrl(skvelkoSet.id)),
    numberOfItems: includesObject.length,
    includesObject,
  };
}

export enum SearchProductType {
  PRODUCT = 'PRODUCT',
  PRODUCT_SOLD_OUT = 'PRODUCT_SOLD_OUT',
  SKVELKO_SET = 'SKVELKO_SET',
}

export interface SearchProductItem {
  type: SearchProductType;
  position: number;
  item: any;
}

export function concatSearchProductsAndSkvelkoSetsAndSoldOutProducts(
  products,
  skvelkoSets,
  soldOutProducts,
  positions,
): SearchProductItem[] {
  const items: SearchProductItem[] = [];
  if (products) {
    let defaultPosition = 1;
    for (const product of products) {
      let position = defaultPosition;
      if (positions && positions.products_positions) {
        const productPosition = positions.products_positions.find(
          (pp) => pp.product_id == product.product_id,
        );
        if (productPosition) {
          position = productPosition.position;
        }
      }

      if (!positions) {
        defaultPosition++;
      }

      items.push({
        type: SearchProductType.PRODUCT,
        position: position,
        item: product,
      });
    }
  }

  if (skvelkoSets) {
    for (const skvelkoSet of skvelkoSets) {
      let position = 999;
      if (positions && positions.skvelko_sets_positions) {
        const skvelkoSetPosition = positions.skvelko_sets_positions.find(
          (skp) => skp.id === skvelkoSet.id,
        );
        if (skvelkoSetPosition) {
          position = skvelkoSetPosition.position;
        }
      }

      items.push({
        type: SearchProductType.SKVELKO_SET,
        position: position,
        item: skvelkoSet,
      });
    }
  }

  if (soldOutProducts?.length) {
    items.forEach((item) => {
      item.position += soldOutProducts?.length;
    });
    let position = 1;
    for (const soldOutProduct of soldOutProducts) {
      items.push({
        type: SearchProductType.PRODUCT_SOLD_OUT,
        position: position,
        item: soldOutProduct,
      });
      position++;
    }
  }

  items.sort((a, b) => (a.position > b.position ? 1 : -1));
  return items;
}
